import { Component } from '@theme/component';
import { onDocumentReady, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  #menuDrawerHiddenWidth = null;
  #intersectionObserver = null;
  #offscreen = false;
  #lastScrollTop = 0;
  #timeout = null;
  #animationDelay = 150;

  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);
document.body.style.setProperty('--transparent-header-offset', `${height}px`);

    // novo: recalcula também o offset real do topo
    updateTransparentHeaderOffset();

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }

      // novo
      updateTransparentHeaderOffset();
    }, config);

    this.#intersectionObserver.observe(this);
  };

  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }

    updateTransparentHeaderOffset();
  }

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isScrollingUp) {
        if (this.getBoundingClientRect().top >= 0) {
          this.dataset.scrollDirection = 'none';
        } else {
          this.dataset.scrollDirection = 'up';
        }
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      updateTransparentHeaderOffset();
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
        updateTransparentHeaderOffset();
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
    updateTransparentHeaderOffset();
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      }
    }

    updateTransparentHeaderOffset();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');
    document.body.style.setProperty('--transparent-header-offset', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentReady(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => {
      calculateHeaderGroupHeight(header, headerGroup);
      updateTransparentHeaderOffset();
    });

    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    const mutationObserver = new MutationObserver(() => {
      const children = headerGroup.children;
      for (let i = 0; i < children.length; i++) {
        const element = children[i];
        if (element === header || !(element instanceof HTMLElement)) continue;
        resizeObserver.observe(element);
      }

      updateTransparentHeaderOffset();
    });

    mutationObserver.observe(headerGroup, { childList: true, subtree: true });
  }

  window.addEventListener('load', updateTransparentHeaderOffset);
  window.addEventListener('resize', updateTransparentHeaderOffset, { passive: true });

  // roda algumas vezes após o load porque alguns componentes ainda alteram altura depois
  setTimeout(updateTransparentHeaderOffset, 50);
  setTimeout(updateTransparentHeaderOffset, 200);
  setTimeout(updateTransparentHeaderOffset, 600);
});

function calculateHeaderGroupHeight(header, headerGroup) {
  if (!(headerGroup instanceof HTMLElement)) return;

  let totalHeight = 0;
  const children = headerGroup.children;

  for (let i = 0; i < children.length; i++) {
    const element = children[i];
    if (!(element instanceof HTMLElement)) continue;
    totalHeight += element.getBoundingClientRect().height;
  }

  document.body.style.setProperty('--header-group-height', `${totalHeight}px`);
}

function updateTransparentHeaderOffset() {
  const header = document.querySelector('#header-component');

  if (!(header instanceof HTMLElement)) {
    document.body.style.setProperty('--transparent-header-offset', '0px');
    return;
  }

  if (!header.hasAttribute('transparent')) {
    document.body.style.setProperty('--transparent-header-offset', '0px');
    return;
  }

  const height = Math.round(header.getBoundingClientRect().height);
  document.body.style.setProperty('--transparent-header-offset', `${height}px`);
}